import React, { useContext, useEffect, useState } from 'react'
import { Table, Button, Typography, Affix, App, Modal, Form, Space, Switch, Image, Avatar } from 'antd'
import { isMobileOnly, isDesktop } from "react-device-detect"
import { useNavigate, Link } from "react-router-dom"
import _ from "lodash"
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaEdit, FaDownload, FaImage, FaTrash, FaSearch } from "react-icons/fa"
import { API, Enum, Font } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

const currency = new Intl.NumberFormat()
const { Pages: { TicketManagement: { Price: PriceLanguage } }, Enum: EnumLanguage, Share, Message: MessageLanguage } = language
const {  TICKET_MANAGEMENT_PRICE_PAGINATE, TICKET_MANAGEMENT_PRICE_REMOVE } = API
const { Message, Payment, Component } = Enum
const { IRANSans, IRANSansFaNum } = Font

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface PriceType {
    row: number,
    id: string,
    key: string,
    destination: CityType,
    price: number,
    createdAt: Date,
    updatedAt: Date,
}

export default function Index() {

    const [formModal] = Form.useForm()
    const navigate = useNavigate()
    const { accessToken, baseURL } = useContext(GlobalContext)
    const [prices, setPrices] = useState<PriceType[]>([])
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false)
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    })
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp()

    const priceColumns: ColumnsType<PriceType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: PriceLanguage.destination,
            dataIndex: "destination",
            render: (text, record) => text && `${text.name} (${text.province.name})`
        },
        {
            title: PriceLanguage.price,
            dataIndex: "price",
            key: "price",
            render: (text, record) => text.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' ' + Share.rial
        },
        {
            title: PriceLanguage.createdAt,
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('HH:mm YYYY/MM/DD')
        },
        {
            title: PriceLanguage.updatedAt,
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('HH:mm YYYY/MM/DD')
        },
        {
            title: Share.edit,
            dataIndex: "edit",
            key: "edit",
            render: (text, record) =>  <Link to={'/ticket-management/price/edit/' + record.id}><FaEdit size={20} /></Link>
        },
        {
            title: Share.delete,
            dataIndex: "delete",
            key: "delete",
            render: (text, record) =>  (
                <Typography.Link onClick={() => modal.confirm({
                    title: Share.deleteConfirmTitle,
                    okText: Share.deleteConfirmOk,
                    okType: 'danger',
                    cancelText: Share.deleteConfirmCancel,
                    onOk() {
                        setLoadingRecords(true)
                        axios.delete(TICKET_MANAGEMENT_PRICE_REMOVE + record.id).then((response: {}) => {
                            const { total = 1, pageSize = 1, current = 1 } = pagination
                            const params = {
                                page: total % pageSize !== 1 ? current : current - 1,
                                limit: pageSize,
                            }
                            getPrices(params)
                            message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                        }).catch((error: AxiosError) => {
                            const { response: { data } } = error
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response )
                            setLoadingRecords(false)
                        })
                    }
                })}><FaTrash size={20} /></Typography.Link>
            )
        }
    ]

    const getPrices = (params: AntTableParams) => {
        setLoadingRecords(true)
        axios.post(TICKET_MANAGEMENT_PRICE_PAGINATE, {}, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: prices, page: current, limit: pageSize, totalDocs: total } } = response
            let row = (current - 1) * pageSize
            setPrices(prices.map<PriceType>((price: PriceType) => ({ ...price, row: ++row, key: price.id })))
            setLoadingRecords(false)
            setPagination({
                ...pagination,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    useEffect(() => {
        const { current, pageSize } = pagination
        getPrices({ page: current, limit: pageSize })
    }, [])

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filtersTable: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getPrices({ page: current, limit: pageSize, sort })
    }

    return (
        <>
            <Table
                columns={priceColumns}
                dataSource={prices}
                pagination={pagination}
                loading={loadingRecords}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Space.Compact block style={{marginTop:'10px'}}>
                    <Button
                        key='add'
                        type="primary"
                        icon={<FaPlus />}
                        style={{height:'40px'}}
                        onClick={() => navigate('/ticket-management/price/add')}>
                        {PriceLanguage.addPrice}
                    </Button>
                </Space.Compact>
            </Affix>
        </>
    )
}
